import { Plane, Ticket, CreditCard, Luggage } from 'lucide-react';
import { FlightSegmentCard } from '../cards/FlightSegmentCard';
import { TicketCard } from '../cards/TicketCard';
import { FrequentFlyerCard } from '../cards/FrequentFlyerCard';
import { SectionHeader, EmptyState } from '../shared/InfoGrid';
import { getTravelerName } from '../utils/formatters';
import type { BookingData, Dictionaries, TravelDocument, FrequentFlyerCard as FFCardType, BaggageAllowance } from '../utils/types';

interface TravelDataTabProps {
  data: BookingData;
  dictionaries: Dictionaries;
}

export function TravelDataTab({ data, dictionaries }: TravelDataTabProps) {
  const { air, travelers, travelDocuments, frequentFlyerCards, specialServiceRequests } = data;
  const flights = dictionaries.flight || {};

  // Build SSR to flight mapping for FF cards
  const ssrFlightMap: Record<string, string> = {};
  if (specialServiceRequests) {
    specialServiceRequests.forEach(ssr => {
      if (ssr.code === 'FQTV' && ssr.flightIds) {
        ssr.flightIds.forEach(flightId => {
          ssrFlightMap[ssr.id] = flightId;
        });
      }
    });
  }

  // Sort travel documents by date (most recent first)
  const sortedDocuments = [...(travelDocuments || [])].sort((a, b) => {
    const dateA = new Date(a.creation?.localDateTime || 0);
    const dateB = new Date(b.creation?.localDateTime || 0);
    if (dateB.getTime() === dateA.getTime()) {
      return (b.id || '').localeCompare(a.id || '');
    }
    return dateB.getTime() - dateA.getTime();
  });

  // Get related tickets and FF cards for a flight segment
  const getRelatedTickets = (flightId: string): TravelDocument[] => {
    return travelDocuments?.filter(
      doc => doc.flightIds && doc.flightIds.includes(flightId)
    ) || [];
  };

  const getRelatedFFCards = (flightId: string): FFCardType[] => {
    return frequentFlyerCards?.filter(card => {
      const cardIds = card.id.split(':');
      return cardIds.some(id => ssrFlightMap[id] === flightId);
    }) || [];
  };

  const hasBounds = air?.bounds && air.bounds.length > 0;
  const hasDocuments = sortedDocuments.length > 0;
  const hasFFCards = frequentFlyerCards && frequentFlyerCards.length > 0;
  const hasBaggage = air?.freeCheckedBaggageAllowanceItems && air.freeCheckedBaggageAllowanceItems.length > 0;

  // Format baggage allowance for display
  const formatBaggageAllowance = (baggage: BaggageAllowance): string => {
    if (baggage.details.type === 'piece') {
      return `${baggage.details.quantity} piece${baggage.details.quantity !== 1 ? 's' : ''}`;
    } else {
      return `${baggage.details.quantity} kg`;
    }
  };

  // Get flight route description for baggage
  const getBaggageFlightInfo = (flightIds: string[]): string => {
    if (!flightIds || flightIds.length === 0) return 'All flights';
    const flightDescriptions = flightIds.map(id => {
      const flight = flights[id];
      if (!flight) return id;
      return `${flight.marketingAirlineCode}${flight.marketingFlightNumber}`;
    });
    return flightDescriptions.join(', ');
  };

  if (!hasBounds && !hasDocuments && !hasFFCards) {
    return (
      <EmptyState
        icon={<Plane className="h-6 w-6" />}
        title="No travel data available"
        description="This booking doesn't contain any flight or ticket information"
      />
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
      {/* Left column: Flight segments */}
      <div>
        <SectionHeader
          title="Flight Segments"
          icon={<Plane className="h-5 w-5" />}
          count={air?.bounds?.reduce((acc, b) => acc + b.flights.length, 0)}
        />

        {hasBounds ? (
          <div className="space-y-6">
            {air!.bounds.map((bound, boundIdx) => (
              <div key={bound.airBoundId || boundIdx}>
                {/* Bound header */}
                <div className="flex items-center gap-2 mb-3 text-sm">
                  <span className="font-semibold text-slate-700">
                    {bound.originLocationCode}
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className="font-semibold text-slate-700">
                    {bound.destinationLocationCode}
                  </span>
                  <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded ml-2">
                    Bound {bound.airBoundId}
                  </span>
                </div>

                {/* Flights in bound */}
                <div className="space-y-1">
                  {bound.flights.map((segment, segIdx) => {
                    const flightDetails = flights[segment.id];
                    if (!flightDetails) return null;

                    const isLast = segIdx === bound.flights.length - 1;

                    return (
                      <FlightSegmentCard
                        key={segment.id}
                        segment={segment}
                        flightDetails={flightDetails}
                        dictionaries={dictionaries}
                        relatedTickets={getRelatedTickets(segment.id)}
                        relatedFFCards={getRelatedFFCards(segment.id)}
                        showConnectionTime={!isLast}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Plane className="h-5 w-5" />}
            title="No flights"
          />
        )}
      </div>

      {/* Right column: Tickets and FF cards */}
      <div className="space-y-4 md:space-y-8">
        {/* Tickets section */}
        <div>
          <SectionHeader
            title="Tickets"
            icon={<Ticket className="h-5 w-5" />}
            count={sortedDocuments.length}
          />

          {hasDocuments ? (
            <div className="space-y-3">
              {sortedDocuments.map(ticket => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  travelers={travelers || []}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Ticket className="h-5 w-5" />}
              title="No tickets"
            />
          )}
        </div>

        {/* Frequent Flyer Cards section */}
        <div>
          <SectionHeader
            title="Frequent Flyer Cards"
            icon={<CreditCard className="h-5 w-5" />}
            count={frequentFlyerCards?.length}
          />

          {hasFFCards ? (
            <div className="space-y-3">
              {frequentFlyerCards!.map(card => (
                <FrequentFlyerCard
                  key={card.id}
                  card={card}
                  travelers={travelers || []}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<CreditCard className="h-5 w-5" />}
              title="No frequent flyer cards"
            />
          )}
        </div>

        {/* Baggage Allowance section */}
        {hasBaggage && (
          <div>
            <SectionHeader
              title="Checked Baggage Allowance"
              icon={<Luggage className="h-5 w-5" />}
              count={air!.freeCheckedBaggageAllowanceItems!.length}
            />

            <div className="space-y-2">
              {air!.freeCheckedBaggageAllowanceItems!.map((baggage, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between rounded-lg bg-white border border-slate-200 p-3 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <Luggage className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="font-medium text-slate-700">
                        {formatBaggageAllowance(baggage)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {baggage.travelerIds?.map(id => getTravelerName(travelers || [], id)).join(', ') || 'All travelers'}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">
                    {getBaggageFlightInfo(baggage.flightIds)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
